import { useAuth } from '@clerk/react';
import { Link } from 'react-router-dom';
import { useChecklists } from '../hooks';
import { NudgeButton } from '../components/NudgeButton';
import type { ChecklistStep, AssignedChecklist } from '../types';

export function MyChecklistsPage() {
    const { userId } = useAuth();

    // Fetch active checklists for the current user
    const { data, isLoading } = useChecklists({ 
        status: 'active',
        subject_user_id: userId ?? undefined
    });

    const checklists = data?.data ?? [];

    // Gather pending (not completed / not skipped) steps
    const pendingByChecklist = checklists
        .map((c) => ({
            checklist: c,
            pendingSteps: (c.steps ?? []).filter(
                (s) => s.status !== 'completed' && s.status !== 'skipped'
            ),
        }))
        .filter((g) => g.pendingSteps.length > 0);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold">My Checklists</h1>
                <p className="text-sm text-muted-foreground">
                    Your pending steps across all active checklists
                </p>
            </div>

            {isLoading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : pendingByChecklist.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center">
                    <p className="text-muted-foreground">
                        🎉 You're all caught up — no pending steps.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {pendingByChecklist.map(({ checklist, pendingSteps }) => (
                        <ChecklistGroup
                            key={checklist.id}
                            checklist={checklist}
                            steps={pendingSteps}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ChecklistGroup({
    checklist,
    steps,
}: {
    checklist: AssignedChecklist;
    steps: ChecklistStep[];
}) {
    const overdue = steps.filter(
        (s) => new Date(s.due_date) < new Date() && s.status !== 'blocked'
    );

    return (
        <div className="rounded-lg border">
            {/* Checklist header */}
            <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-3">
                <div>
                    <Link
                        to={`/checklists/${checklist.id}`}
                        className="font-medium hover:underline"
                    >
                        {checklist.template_name}
                    </Link>
                    {checklist.progress && (
                        <p className="text-xs text-muted-foreground">
                            {checklist.progress.completed}/{checklist.progress.total} steps
                            done ({checklist.progress.percent}%)
                        </p>
                    )}
                </div>
                {overdue.length > 0 && (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                        {overdue.length} overdue
                    </span>
                )}
            </div>

            {/* Steps */}
            <div className="divide-y">
                {steps.map((step) => {
                    const isOverdue =
                        new Date(step.due_date) < new Date() && step.status !== 'blocked';
                    return (
                        <div
                            key={step.id}
                            className="flex items-center justify-between px-4 py-3"
                        >
                            <div className="flex-1">
                                <p className="text-sm font-medium">{step.title}</p>
                                <p className="text-xs text-muted-foreground">
                                    Due{' '}
                                    {new Date(step.due_date).toLocaleDateString('en-GB')} ·{' '}
                                    {step.assignee_name}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${step.status === 'blocked'
                                            ? 'bg-yellow-50 text-yellow-700'
                                            : step.status === 'in_progress'
                                                ? 'bg-blue-50 text-blue-700'
                                                : isOverdue
                                                    ? 'bg-red-50 text-red-700'
                                                    : 'bg-gray-100 text-gray-500'
                                        }`}
                                >
                                    {isOverdue && step.status === 'not_started'
                                        ? 'overdue'
                                        : step.status.replace(/_/g, ' ')}
                                </span>
                                <NudgeButton
                                    checklistId={checklist.id}
                                    stepId={step.id}
                                    assigneeName={step.assignee_name}
                                />
                                <Link to={`/checklists/${checklist.id}`}>
                                    <span className="text-xs text-primary hover:underline">
                                        Open →
                                    </span>
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}